import "./styles.css";

import {
  createBrowserCameraPort,
  createBrowserCapabilityPort,
  createBrowserEngineLoader,
  createBrowserEventPort,
  createBrowserReturnPort
} from "./browser-ports.js";
import { WebArFallbackController } from "./controller.js";
import {
  MemoryLaunchReplayGuard,
  createBrowserLaunchFragmentPort,
  createSignedLaunchResolver
} from "./launch.js";
import { createVerifiedGlbModelLoader } from "./model-loader.js";
import { createDomWebArView } from "./ui.js";
import { createXr8ThreeRuntime } from "./xr8-runtime.js";

function trustedReferrerOrigin(): string | undefined {
  if (!document.referrer) return undefined;
  try {
    const url = new URL(document.referrer);
    return url.protocol === "https:" ? url.origin : undefined;
  } catch {
    return undefined;
  }
}

function fixedSameOriginUrl(metaName: string, fallbackPath: string): string {
  const configured = document.querySelector<HTMLMetaElement>(`meta[name="${metaName}"]`)?.content;
  const url = new URL(configured || fallbackPath, window.location.origin);
  if (url.origin !== window.location.origin || url.username || url.password || url.hash) {
    throw new Error(`${metaName} must resolve to this deployment origin.`);
  }
  return url.href;
}

const root = document.querySelector<HTMLElement>("#app");
if (!root) throw new Error("The WebAR mount element is missing.");

const view = createDomWebArView(root);
const requestOrigin = trustedReferrerOrigin();
const launchEndpoint = fixedSameOriginUrl(
  "customiznator-ar-launch-endpoint",
  "/v1/ar/fallback/session"
);
const engineUrl = fixedSameOriginUrl(
  "customiznator-xr8-engine-url",
  "/external/xr/xr.js"
);
const replay = new MemoryLaunchReplayGuard();
const controller = new WebArFallbackController({
  launches: createSignedLaunchResolver({
    endpoint: launchEndpoint,
    fragment: createBrowserLaunchFragmentPort(window.location, window.history),
    replay,
    fetch,
    validation: () => ({
      now: new Date(),
      trustedModelOrigins: [window.location.origin],
      trustedRequestOrigins: requestOrigin
        ? [requestOrigin, window.location.origin]
        : [window.location.origin]
    })
  }),
  capabilities: createBrowserCapabilityPort(),
  camera: createBrowserCameraPort(),
  engine: createBrowserEngineLoader({ window, document, engineUrl }),
  models: createVerifiedGlbModelLoader(),
  runtime: createXr8ThreeRuntime(),
  view,
  events: createBrowserEventPort(),
  returns: createBrowserReturnPort()
});

// The customer's original AR click is the intent. Start immediately like a
// native viewer; a minimal retry appears only when the browser requires a new
// gesture for camera permission.
void controller.start();

window.addEventListener("pagehide", () => { void controller.dispose(); }, { once: true });
